import { InputCopy } from "@/components/molecules/input-copy";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
} from "@/components/molecules/responsive-dialog";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import { DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useCreateApiKey } from "@/features/auth/hooks/use-api-keys";
import { useUser } from "@/features/auth/hooks/use-user";
import { useEffect, useState } from "react";
import { useTranslation } from "@/features/utils/i18n";
import { toast } from "sonner";
import Env from "../../../../config/environment";

interface AddApiKeyDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddApiKeyDrawer = ({
  open,
  onOpenChange,
}: AddApiKeyDrawerProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [generatedSecret, setGeneratedSecret] = useState<string | null>(null);
  const { data: user } = useUser();
  const createApiKeyMutation = useCreateApiKey();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(t("security.apiKeys.pleaseProvideName"));
      return;
    }

    try {
      const result = await createApiKeyMutation.mutateAsync({ name });
      setGeneratedSecret(result.secret);
    } catch {
      // Error is handled by the mutation
    }
  };

  const copyAuthHeader = async () => {
    if (generatedSecret && user?.id) {
      const authValue = btoa(`${user.id}:${generatedSecret}`);
      const headerValue = `Basic ${authValue}`;
      await navigator.clipboard.writeText(headerValue);
      toast.success(t("security.apiKeys.authHeaderCopied"));
    }
  };

  const handleClose = () => {
    setName("");
    setGeneratedSecret(null);
    onOpenChange(false);
  };

  // Reset state when drawer opens
  useEffect(() => {
    if (open && !generatedSecret) {
      setName("");
    }
  }, [open, generatedSecret]);

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="sm:max-w-md">
        <ResponsiveDialogHeader>
          <DialogTitle>
            {generatedSecret
              ? t("security.apiKeys.createdTitle")
              : t("security.apiKeys.createTitle")}
          </DialogTitle>
          <DialogDescription>
            {generatedSecret
              ? t("security.apiKeys.createdDescription")
              : t("security.apiKeys.createDescription")}
          </DialogDescription>
        </ResponsiveDialogHeader>

        <CardContent className="space-y-4">
          {!generatedSecret ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t("security.apiKeys.nameLabel")}</Label>
                <Input
                  id="name"
                  placeholder={t("security.apiKeys.namePlaceholder")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={createApiKeyMutation.isPending}
                />
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t("security.apiKeys.secretLabel")}</Label>
                <InputCopy
                  value={generatedSecret}
                  toastMessage={t("security.apiKeys.secretCopied")}
                />
              </div>

              <Separator />

              <div className="space-y-3">
                <Label className="text-sm font-medium">
                  {t("security.apiKeys.usageExample")}
                </Label>
                <div className="space-y-2 text-sm">
                  <p className="text-muted-foreground">
                    {t("security.apiKeys.usageDescription")}
                  </p>
                  <div className="bg-muted p-3 rounded-sm">
                    <p className="text-xs text-muted-foreground mb-2">
                      {t("security.apiKeys.authHeader")}
                    </p>
                    <code className="text-xs break-all">
                      Basic{" "}
                      {user?.id && generatedSecret
                        ? btoa(`${user.id}:${generatedSecret}`)
                        : "base64(user_id:secret)"}
                    </code>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={copyAuthHeader}
                      className="p-0 h-auto ml-2 text-xs"
                    >
                      {t("common.copy")}
                    </Button>
                  </div>
                  <div className="bg-muted p-3 rounded-sm">
                    <p className="text-xs text-muted-foreground mb-2">
                      {t("security.apiKeys.curlExampleTitle")}
                    </p>
                    <code className="text-xs break-all">
                      curl -H "Authorization: Basic{" "}
                      {user?.id && generatedSecret
                        ? btoa(`${user.id}:${generatedSecret}`)
                        : "base64(user_id:secret)"}
                      " {Env.server}...
                    </code>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>

        <ResponsiveDialogFooter>
          {!generatedSecret ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t("security.apiKeys.cancel")}
              </Button>
              <Button
                type="submit"
                onClick={handleSubmit}
                disabled={createApiKeyMutation.isPending || !name.trim()}
              >
                {createApiKeyMutation.isPending
                  ? t("security.apiKeys.creating")
                  : t("security.apiKeys.create")}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>{t("security.apiKeys.done")}</Button>
          )}
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
};
